import React, { FunctionComponent, ReactElement } from 'react';
import styled from 'styled-components';
import { useMedia } from 'react-media-match';
import { FormattedMessage } from 'react-intl';
import useConfig from '@empiriecom/module-components/hooks/useConfig';
import { FragmentConfiguration } from '@/config';

const StyledPaymentPause = styled.div<{
  cardLayout: boolean;
  isMobile: boolean;
  borderColor: string;
}>`
  display: flex;
  justify-content: flex-start;
  align-items: ${({ isMobile }) => (isMobile ? 'flex-start' : 'center')};
  ${({ isMobile, cardLayout, borderColor }) =>
    isMobile || cardLayout
      ? `margin: 1rem 0; padding: .5625rem 0; border-top: .0625rem solid ${borderColor}; border-bottom: .0625rem solid ${borderColor};`
      : 'margin-top: 2.125rem; padding-bottom: 2.125rem;'}
`;

const PaymentPauseInputWrapper = styled.div`
  margin-right: 0.6125rem;
  position: relative;
  cursor: pointer;
  border: 0.0625rem solid #000;
  user-select: none;
  border-color: #ccccc9;
  background-image: linear-gradient(to bottom, #fff, #f5f5f5);
  border-radius: 3px;
  width: 1rem;
  height: 1rem;
`;

const PaymentPauseInput = styled.input`
  display: block;
  visibility: hidden;
  position: absolute;
  left: -9999rem;
  top: auto;
  &&:checked + label {
    opacity: 1;
  }
`;

const PaymentPauseInputLabel = styled.label<{ isMobile: boolean }>`
  opacity: 0;
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  cursor: pointer;

  .input-check-icon {
    ${({ isMobile }) =>
      isMobile
        ? 'width: 1.25rem;height: 1.25rem;left:-1px;'
        : 'width: 1.5rem;height: 1.5rem;left:-3px;'}
    position: absolute;
    bottom: 0;
  }
`;

const InputLabelCheck = ({ cssClass }): JSX.Element => (
  <svg className={cssClass} viewBox="0 0 32 32">
    <path d="M3.726 20.052c0 0 3.552 4.296 5.813 9.665 0.968 0.714 2.483 0.068 4.2-2.506 2.058-3.090 12.915-22.196 16.143-23.627 3.229-1.432-1.393-2.726-4.039 0-3.391 3.489-11.139 13.29-12.833 15.706-1.695 2.417-1.937 3.088-2.542 2.014-0.605-1.073-1.816-3.893-2.663-4.162-0.848-0.267-0.969 0-2.059 0.673-1.092 0.669-4.159 2.058-2.020 2.237z" />
  </svg>
);

const PaymentPauseDescription = styled.label`
  cursor: pointer;
`;

export interface PaymentPauseProps {
  /** Flag indicating if payment pause is selected. */
  paymentPause: boolean;
  /** Handler for selection change. */
  onChange: () => void;
  /** Border color value. */
  borderColor: string;
}

export const PaymentPause: FunctionComponent<PaymentPauseProps> = ({
  paymentPause,
  onChange,
  borderColor,
}): ReactElement => {
  const mobile = useMedia({ desktop: false, mobile: true, tablet: false });
  const {
    installmentCalculator: { cardLayout },
  } = useConfig<FragmentConfiguration>();
  return (
    <StyledPaymentPause borderColor={borderColor} cardLayout={cardLayout} isMobile={mobile}>
      <PaymentPauseInputWrapper>
        <PaymentPauseInput
          checked={paymentPause}
          data-testid="payment-pause-input"
          id="payment-pause"
          onChange={onChange}
          type="checkbox"
        />
        <PaymentPauseInputLabel htmlFor="payment-pause" isMobile={mobile}>
          <InputLabelCheck cssClass="input-check-icon" />
        </PaymentPauseInputLabel>
      </PaymentPauseInputWrapper>
      <PaymentPauseDescription htmlFor="payment-pause">
        <FormattedMessage
          id="installment-calculator.payment-pause-label.text1"
          defaultMessage="Ich möchte die"
        />
        <span>&#160;</span>
        <strong>
          <FormattedMessage
            id="installment-calculator.payment-pause-label.text2"
            defaultMessage="Zahlpause"
          />
        </strong>
        <span>&#160;</span>
        <FormattedMessage
          id="installment-calculator.payment-pause-label.text3"
          defaultMessage="nutzen &amp; zahle erst in 4 Monaten*"
        />
      </PaymentPauseDescription>
    </StyledPaymentPause>
  );
};

export default PaymentPause;
